import * as React from 'react';
import {useEffect, useState, useCallback} from 'react';
import {CircularProgress} from "@mui/joy";
import Box from "@mui/joy/Box";
import ChatsPane from "./ChatsPane";
import MessagesPane from "./MessagesPane";
import {GetUnifiedChats, UpdateReadStatus, UpdateWhatsAppReadStatus, UpdateEmailReadStatus} from "../../../axios/Axios";

export type ChannelType = 'sms' | 'whatsapp' | 'email';

interface MessageProps {
    id: string;
    content: string;
    timestamp: string;
    sender: any;
    unread?: boolean;
}

export interface ChatProps {
    id: string;
    sender: any;
    messages: MessageProps[];
    has_sms?: boolean;
    has_whatsapp?: boolean;
    has_email?: boolean;
    unread_sms?: boolean;
    unread_whatsapp?: boolean;
    unread_email?: boolean;
    channel?: string;
    call_category?: string;
    // Latest booking (if any) for this customer. Drives the booking-status
    // chip shown in the chat list row and the Messages pane header.
    latest_booking_id?: number | null;
    latest_booking_status?: string | null;
    latest_booking_booked_in?: string | null;
    has_ronald_intervention?: boolean;
}

function useDebounce(value: string, delay: number) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
}

// Local storage key for category filter (shared across messages/voicemails)
const CATEGORY_FILTER_KEY = 'messagesCategoryFilter';

function getDefaultChannel(chat: ChatProps): ChannelType {
    // If the latest message has a known channel, prefer that
    if (chat.channel === 'sms' || chat.channel === 'whatsapp' || chat.channel === 'email') {
        return chat.channel;
    }
    if (chat.has_sms) return 'sms';
    if (chat.has_whatsapp) return 'whatsapp';
    if (chat.has_email) return 'email';
    return 'sms';
}

export default function Messages() {
    const [selectedChat, setSelectedChat] = React.useState<ChatProps | null>(null);
    const [activeChannel, setActiveChannel] = React.useState<ChannelType>('sms');

    const [chats, setChats] = React.useState<ChatProps[]>([]);
    const [chatListPage, setChatListPage] = React.useState(1);
    const [hasMoreChats, setHasMoreChats] = React.useState(true);
    const [isFetchingChats, setIsFetchingChats] = React.useState(true);

    const [searchTerm, setSearchTerm] = React.useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    const [category, setCategory] = React.useState(() => {
        return localStorage.getItem(CATEGORY_FILTER_KEY) || 'All';
    });

    const handleCategoryChange = React.useCallback((newCategory: string) => {
        setCategory(newCategory);
        localStorage.setItem(CATEGORY_FILTER_KEY, newCategory);
        setChatListPage(1);
    }, []);

    const fetchingRef = React.useRef(false);
    const fetchChats = useCallback(async (searchQuery: string, pageNum: number, categoryFilter: string) => {
        if (fetchingRef.current) return;
        fetchingRef.current = true;
        setIsFetchingChats(true);
        try {
            const location = localStorage.getItem('location')?.toLowerCase().replace(/ /g, '_');
            const params: any = {location, search: searchQuery, page: pageNum};
            if (categoryFilter && categoryFilter !== 'All') {
                params.category = categoryFilter;
            }
            const response = await GetUnifiedChats(params);
            const {chats: newChats, hasMore} = response.data;
            setChats(prev => {
                if (pageNum === 1) return newChats;
                const existingIds = new Set(prev.map(c => c.id));
                const unique = newChats.filter((c: ChatProps) => !existingIds.has(c.id));
                return [...prev, ...unique];
            });
            setHasMoreChats(hasMore);
        } catch (error) {
            console.error("Failed to fetch chats:", error);
            if (pageNum === 1) {
                setChats([]);
                setHasMoreChats(false);
            }
        } finally {
            fetchingRef.current = false;
            setIsFetchingChats(false);
        }
    }, []);

    useEffect(() => {
        if (chatListPage > 1) {
            fetchChats(debouncedSearchTerm, chatListPage, category);
        }
    }, [chatListPage, debouncedSearchTerm, fetchChats, category]);

    useEffect(() => {
        setChatListPage(1);
        fetchChats(debouncedSearchTerm, 1, category);
    }, [debouncedSearchTerm, fetchChats, category]);

    useEffect(() => {
        // If current selection is no longer in the list (e.g. after opt-out), clear it
        if (selectedChat && !chats.find(c => c.id === selectedChat.id)) {
            setSelectedChat(null);
        }
    }, [chats, selectedChat]);

    const loadMoreChats = useCallback(() => {
        setChatListPage(prevPage => prevPage + 1);
    }, []);

    const handleSetSelectedChat = useCallback((chat: ChatProps) => {
        setSelectedChat(chat);
        setActiveChannel(getDefaultChannel(chat));
    }, []);

    const handleMarkChannelRead = useCallback((channel: 'sms' | 'whatsapp' | 'email', chatId: string) => {
        if (selectedChat?.id !== chatId) return;
        const updateChatAndSelection = (field: 'unread_sms' | 'unread_whatsapp' | 'unread_email') => {
            setChats(prevChats => prevChats.map(c => c.id === chatId ? {...c, [field]: false} : c));
            setSelectedChat(prev => (prev && prev.id === chatId ? {...prev, [field]: false} : prev));
        };
        if (channel === 'sms') {
            UpdateReadStatus(chatId).then(() => updateChatAndSelection('unread_sms')).catch(err => console.error("Failed to update SMS read status:", err));
        } else if (channel === 'whatsapp') {
            UpdateWhatsAppReadStatus(chatId).then(() => updateChatAndSelection('unread_whatsapp')).catch(err => console.error("Failed to update WhatsApp read status:", err));
        } else if (channel === 'email') {
            UpdateEmailReadStatus(chatId).then(() => updateChatAndSelection('unread_email')).catch(err => console.error("Failed to update email read status:", err));
        }
    }, [selectedChat?.id]);

    const handleOptOut = useCallback(() => {
        setSelectedChat(null);
        setChatListPage(1);
        fetchChats(debouncedSearchTerm, 1, category);
    }, [fetchChats, debouncedSearchTerm, category]);

    const handleRefreshChatList = useCallback(() => {
        // Re-pull the first page so the sidebar's preview snippet reflects
        // the latest inbound/outbound message (e.g. after Ronald replies).
        setChatListPage(1);
        fetchChats(debouncedSearchTerm, 1, category);
    }, [fetchChats, debouncedSearchTerm, category]);

    return (
        <Box sx={{flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, width: '100%'}}>
            {isFetchingChats && chats.length === 0 ? (
                <Box sx={{display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', width: '100%', flex: 1, justifyContent: 'center'}}>
                    <CircularProgress size="lg"/>
                </Box>
            ) : (
                <Box
                    sx={{
                        flex: 1, width: '100%', mx: 'auto',
                        display: 'flex',
                        minHeight: 0,
                        border: '1px solid',
                        borderColor: '#e5e7eb',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        bgcolor: '#fff',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    }}
                >
                    {/* Chat list sidebar — 1/3 width */}
                    <Box
                        sx={{
                            display: {xs: selectedChat ? 'none' : 'flex', sm: 'flex'},
                            flexDirection: 'column',
                            minHeight: 0,
                            overflow: 'hidden',
                            width: {xs: '100%', sm: '33%'},
                            minWidth: 300,
                            maxWidth: 420,
                        }}
                    >
                        <ChatsPane
                            chats={chats}
                            selectedChatId={selectedChat?.id || ''}
                            setSelectedChat={handleSetSelectedChat}
                            searchTerm={searchTerm}
                            onSearchChange={setSearchTerm}
                            category={category}
                            onCategoryChange={handleCategoryChange}
                            loadMoreChats={loadMoreChats}
                            hasMoreChats={hasMoreChats}
                            isFetchingChats={isFetchingChats}
                        />
                    </Box>
                    {/* Chat area — flex-1 */}
                    <Box sx={{display: {xs: selectedChat ? 'flex' : 'none', sm: 'flex'}, flex: 1, flexDirection: 'column', minHeight: 0, minWidth: 0, bgcolor: '#f9fafb'}}>
                        <MessagesPane
                            chat={selectedChat}
                            activeChannel={activeChannel}
                            onChannelChange={setActiveChannel}
                            onOptOut={handleOptOut}
                            onMarkChannelRead={handleMarkChannelRead}
                            onBack={() => setSelectedChat(null)}
                            onRefreshChatList={handleRefreshChatList}
                        />
                    </Box>
                </Box>
            )}
        </Box>
    );
}
